<!-- Markers are triangular shaped controls -->
<template :wxid="$options.name">
  <v-menu
    v-model="isMarkerActive"
    :close-on-content-click="false"
    :left="isMarkerInTheLastThirdOfTheChart"
    :nudge-left="isMarkerInTheLastThirdOfTheChart ? 30 : null"
    :nudge-right="!isMarkerInTheLastThirdOfTheChart ? 10 : null"
    :content-class="
      isMarkerInTheLastThirdOfTheChart ? `marker-dialog marker-dialog--left` : `marker-dialog marker-dialog--right`
    "
    :min-width="minimumPopupWidth"
    rounded="lg"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        @click="toggleSelectedMarker"
        :class="{ 'marker-btn--active': isMarkerActive }"
        :style="{ left: leftPosition(marker.start) }"
        :ripple="false"
        class="marker-btn"
        depressed
        plain
      >
        <span class="marker-btn__caret" />
        <span v-if="isConversionFactorOverride" class="marker-btn__cfo">{{ marker.label }}</span>
      </v-btn>
    </template>

    <v-card tag="article" class="timeline-marker-card px-2" rounded="lg">
      <v-card-title tag="header" class="px-0 pb-3">
        <h4 v-if="isProductionRunCreation" :title="productLabel" class="heading wx-typo-sm text-truncate">
          {{ productLabel }}
        </h4>
        <h4 v-if="isConversionFactorOverride" class="heading wx-typo-sm">
          {{ $t("dashboard.conversionFactorOverride.title") }}
        </h4>
        <v-btn-toggle class="justify-space-between">
          <wx-btn-icon
            :title="$t('common.previous')"
            :disabled="previousMarkerDisabled"
            color="primary"
            class="nav-btn nav-btn--left"
            @click="togglePreviousMarker"
            small
            text
          >
            <v-icon>mdi-chevron-left</v-icon>
          </wx-btn-icon>
          <wx-btn-icon
            :title="$t('common.next')"
            :disabled="nextMarkerDisabled"
            color="primary"
            class="nav-btn nav-btn--right"
            @click="toggleNextMarker"
            small
            text
          >
            <v-icon>mdi-chevron-right</v-icon>
          </wx-btn-icon>
        </v-btn-toggle>
      </v-card-title>
      <v-card-text v-if="isProductionRunCreation" class="px-2 pb-2">
        <dl v-if="marker.production_properties.planned_quantity" class="data-report">
          <dt class="label">{{ `${$t("dashboard.productionRun.tooltip.plannedQuantity_abbr")}` }}</dt>
          <dd class="data">{{ marker.production_properties.planned_quantity }}</dd>
        </dl>
        <dl v-if="marker.production_properties.work_order_label" class="data-report">
          <dt class="label">{{ `${$t("dashboard.productionRun.tooltip.workOrder_abbr")}` }}</dt>
          <dd class="data">{{ marker.production_properties.work_order_label }}</dd>
        </dl>
        <dl v-if="marker.production_properties.lot_label" class="data-report">
          <dt class="label">{{ `${$t("dashboard.productionRun.tooltip.lot")}` }}</dt>
          <dd class="data">{{ marker.production_properties.lot_label }}</dd>
        </dl>
        <dl v-if="marker.production_properties.number_of_employees" class="data-report">
          <dt class="label">{{ `${$t("dashboard.productionRun.tooltip.employees_abbr")}` }}</dt>
          <dd class="data">{{ marker.production_properties.number_of_employees }}</dd>
        </dl>
        <dl class="data-report pt-1">
          <dt class="label icon-as-label">
            <v-icon>mdi-timer-outline</v-icon>
          </dt>
          <dd class="data">
            <time v-if="isMultiLine">
              {{ startDateLabel }}
              <fragment v-if="endDateLabel">
                <br />
                {{ endDateLabel }}
              </fragment>
            </time>
            <time v-else>
              {{ combinedDateLabels }}
            </time>
          </dd>
        </dl>
      </v-card-text>

      <v-card-text v-if="isConversionFactorOverride" class="px-2 pb-2">
        <!-- Product on the specific Production Unit has a single data source `IN` -->
        <dl class="data-report" v-if="getProductConversionFactors(marker).length === 1">
          <dt class="label-cf-override">{{ $t("dashboard.conversionFactorOverride.defaultValue") }}</dt>
          <dd v-if="getProductConversionFactors(marker).length === 1" class="data">
            <span>{{ getProductConversionFactors(marker)[0].conversion_factor }}</span>
          </dd>
        </dl>
        <!-- Multiple data sources `IN` -->
        <div v-else>
          <dl class="data-report">
            <dt class="label-cf-override">{{ $t("dashboard.conversionFactorOverride.defaultValue") }}</dt>
          </dl>
          <div v-for="cf in getProductConversionFactors(marker)" :key="`${cf.device_id}-${cf.data_source_id}`" class="ml-3">
            <dl class="data-report">
              <dt class="label-ds">
                <span class="font-weight-light font-italic">{{ $t("devices.status.dataSourceId") }}:</span>
                <span class="ml-1">{{ cf.data_source_id }}</span>
              </dt>
              <dd class="data label-cf">
                <span>{{ cf.conversion_factor }}</span>
              </dd>
            </dl>
          </div>
        </div>
        <dl class="data-report">
          <dt v-if="getProductConversionFactors(marker).length === 1" class="label-cf-override">{{ $t("dashboard.conversionFactorOverride.overriddenWith") }}</dt>
          <dt v-else class="label-cf-override">{{ $t("dashboard.conversionFactorOverride.allOverriddenWith") }}</dt>
          <dd class="data">
            <span>{{ marker.label }}</span>
          </dd>
        </dl>
        <dl class="data-report pt-1">
          <dt class="label-cf-override icon-as-label">
            <v-icon>mdi-timer-outline</v-icon>
          </dt>
          <dd class="data">
            <time>
              {{ startDateLabel }}
            </time>
          </dd>
        </dl>
      </v-card-text>

      <v-card-actions tag="footer" class="flex-small-column-gap">
        <modify-production-run-dialog
          v-if="isProductionRunCreation && isMarkerActive && !isPresenter"
          :production-run="marker"
          ref="productSelectionDialog"
          @close="toggleTooltip"
        />
        <delete-production-run-dialog
          v-if="isProductionRunCreation && isMarkerActive && !isPresenter"
          :product-label="productLabel"
          :production-run="marker"
          @close="toggleTooltip"
        />
        <modify-conversion-factor-override-dialog
          v-if="isConversionFactorOverride && isMarkerActive && !isPresenter"
          :conversion-factor-override-marker="marker"
          ref="conversionFactorOverrideDialog"
          @close="toggleTooltip"
        />
        <delete-conversion-factor-override-dialog
          v-if="isConversionFactorOverride && isMarkerActive && !isPresenter"
          :conversion-factor-override-marker="marker"
          ref="conversionFactorOverrideDialog"
          @close="toggleTooltip"
        />
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import * as TimeUtils from "@/store/TimeUtils";
import { mapActions, mapGetters } from "vuex";
import { DateTime } from "luxon";
import DeleteConversionFactorOverrideDialog
  from "@/components/dashboard/conversionfactoroverride/DeleteConversionFactorOverrideDialog.vue";
import DeleteProductionRunDialog from "@/components/dashboard/productionRun/DeleteProductionRunDialog.vue";
import ModifyConversionFactorOverrideDialog
  from "@/components/dashboard/conversionfactoroverride/ModifyConversionFactorOverrideDialog.vue";
import ModifyProductionRunDialog from "@/components/dashboard/productionRun/ModifyProductionRunDialog.vue";
import WxBtnIcon from "@/components/ui/WxBtnIcon.vue";

export default {
  name: "TimelineMarkerDialog",
  components: {
    DeleteConversionFactorOverrideDialog,
    ModifyConversionFactorOverrideDialog,
    ModifyProductionRunDialog,
    WxBtnIcon,
    DeleteProductionRunDialog,
  },
  data() {
    return {
      isMarkerActive: false,
      closeButtonPressed: true,
      fav: true,
      menu: true,
      message: true,
      hints: true,
      dateTimes: null,
      currentProduct: null,
      showModal: false,
    };
  },
  props: {
    coverageDuration: {
      type: Number,
      default: null,
    },
    marker: {
      type: Object,
      default: () => null,
    },
    index: {
      type: Number,
      required: true,
    },
    selectedMarkerId: {
      type: String,
      default: "",
    },
    nextMarkerDisabled: {
      type: Boolean,
      required: true,
    },
    previousMarkerDisabled: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapGetters("dashboard", ["productionUnitProducts", "productionRunCoverage", "activeProductionUnitId"]),
    ...mapGetters("navigation", ["activeFactory"]),
    ...mapGetters("devicesAdmin", ["activeFactoryDataSources"]),
    ...mapGetters("user", ["isPresenter"]),
    isProductionRunCreation() {
      return this.marker.type === "ProductSelection";
    },
    isConversionFactorOverride() {
      return this.marker.type === "ConversionFactorOverride";
    },
    minimumPopupWidth() {
      return this.isConversionFactorOverride ? 320 : 250;
    },
    isMarkerInTheLastThirdOfTheChart() {
      if (!this.coverageDuration) return false;
      const markerStart = parseFloat(this.marker.start);
      return markerStart / this.coverageDuration > 2.0 / 3.0;
    },
    productLabel() {
      return this.buildProductLabel(true);
    },
    productSku() {
      if (this.product_selection) {
        return this.product_selection.sku;
      } else {
        return this.marker.label;
      }
    },
    startMs() {
      const marker = this.marker;
      return !marker.original_start ? DateTime.now().toMillis() : marker.original_start;
    },
    endMs() {
      const marker = this.marker;
      return !marker.original_end ? DateTime.now().toMillis() : marker.original_end;
    },
    isMultiLine() {
      const startDate = DateTime.fromMillis(this.startMs);
      const endDate = DateTime.fromMillis(this.endMs);
      return startDate.toFormat(TimeUtils.DATE_FORMAT) !== endDate.toFormat(TimeUtils.DATE_FORMAT);
    },
    combinedDateLabels() {
      const start = this.millisToString(this.startMs, this.timeLabelFormat);
      const end = this.millisToString(this.endMs, this.timeLabelFormat);
      return `${start} — ${this.marker.original_end ? end : this.$t("dashboard.productionRun.inProduction")}`;
    },
    startDateLabel() {
      return this.millisToString(this.startMs, this.timeLabelFormat);
    },
    endDateLabel() {
      return this.marker.original_end
        ? this.millisToString(this.endMs, this.timeLabelFormat)
        : this.$t("dashboard.productionRun.inProduction");
    },
    timeLabelFormat() {
      return this.isMultiLine ? TimeUtils.DATE_TIME_FORMAT_NO_SECONDS : TimeUtils.TIME_FORMAT_HH_MM;
    },
    startDateTime() {
      return this.millisToDateAndTime(this.startMs);
    },
    endDateTime() {
      return !this.marker.original_end ? null : this.millisToDateAndTime(this.marker.original_end);
    },
  },
  watch: {
    selectedMarkerId() {
      if (this.selectedMarkerId !== this.marker.event_id) {
        this.isMarkerActive = false;
      } else {
        this.isMarkerActive = true;
      }
    },
    marker: {
      immediate: true,
      deep: true,
      handler() {
        this.setDateTimes();
        this.setCurrentProduct();
      },
    },
    productionUnitProducts: {
      immediate: true,
      deep: true,
      handler() {
        this.setCurrentProduct();
      },
    },
  },
  methods: {
    ...mapActions("dashboard", ["deleteProductionRun"]),
    buildProductLabel(useParentheses) {
      const sku = this.productSku;
      if (sku) {
        let description = "";
        if (this.productionUnitProducts) {
          const product = this.findProduct(sku);
          if (product?.description) {
            description = useParentheses ? " (" + product.description + ")" : product.description;
          }
        }
        return `${sku} ${description}`;
      } else {
        return this.$t("product.listing.noData");
      }
    },
    setDateTimes() {
      this.dateTimes = {
        startDateTime: this.startDateTime,
        endDateTime: this.endDateTime,
      };
    },
    setCurrentProduct() {
      if (this.productionUnitProducts) {
        const sku = this.productSku;
        this.currentProduct = this.findProduct(sku);
      }
    },
    millisToDateAndTime(millis) {
      const date = this.millisToDate(millis);
      const time = this.millisToTime(millis);
      const dateTime = this.millisToDateTime(millis);
      return {
        date: date,
        time: time,
        dateTime: dateTime,
      };
    },
    closeDeletionDialog() {
      this.showModal = false;
    },
    toggleTooltip() {
      this.isMarkerActive = !this.isMarkerActive;
    },
    close() {
      this.closeButtonPressed = true;
    },
    toggleSelectedMarker() {
      this.toggleTooltip();
      this.$emit("toggleSelectedMarker", this.marker.event_id);
    },
    togglePreviousMarker() {
      this.toggleTooltip();
      this.$emit("toggleMarkerByIndex", this.index - 1);
    },
    toggleNextMarker() {
      this.toggleTooltip();
      this.$emit("toggleMarkerByIndex", this.index + 1);
    },
    leftPosition(blockStart) {
      return 100 / (this.coverageDuration / blockStart) + "%";
    },
    findProduct(sku) {
      return this.productionUnitProducts.find((p) => p.sku === sku);
    },
    toMillis(date) {
      return TimeUtils.toEpochMillisUTC(date);
    },
    millisToString(date, format) {
      return TimeUtils.fromEpochMillis(date, this.activeFactory.timezone, format);
    },
    millisToDate(millis) {
      return TimeUtils.fromEpochMillis(millis, this.activeFactory.timezone, TimeUtils.DATE_FORMAT);
    },
    millisToTime(millis) {
      return TimeUtils.fromEpochMillis(millis, this.activeFactory.timezone, TimeUtils.TIME_FORMAT_HH_MM);
    },
    millisToDateTime(millis) {
      return TimeUtils.fromEpochMillis(millis, this.activeFactory.timezone, TimeUtils.DATE_TIME_FORMAT);
    },
    getProductConversionFactors(marker) {
      if (this.productionRunCoverage) {
        const productionRun = this.productionRunCoverage
          .map((pr) => ({
            start: DateTime.fromISO(pr.start_date).toMillis(),
            end: pr.end_date ? DateTime.fromISO(pr.end_date).toMillis() : DateTime.now().toMillis(),
            sku: pr.sku,
          }))
          .find((pr) => marker.original_start >= pr.start && marker.original_start < pr.end)
        if (productionRun) {
          const product = this.productionUnitProducts.find((p) => p.sku === productionRun.sku);
          if (product && product.associated_production_units) {
            const puAssociation = product.associated_production_units.find(
              (a) => a.production_unit_id === this.activeProductionUnitId,
            );

            if (puAssociation && puAssociation.conversion_factors && puAssociation.conversion_factors.length > 0) {
              return puAssociation.conversion_factors.filter((cf) => {
                // Keep only the conversion factor linked to a Data Source of role `in`
                const dataSource = this.activeFactoryDataSources.find((ds) => ds.id === cf.data_source_id);
                return dataSource && dataSource.role === "in";
              });
            } else {
              return [
                {
                  conversion_factor: puAssociation?.conversion_factor ?? "1:1",
                  data_source_id: null,
                  device_id: null,
                },
              ];
            }
          }
        }
        return null;
      }
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
// local var
$caretSize: 8px;

// style
.v-application {
  .marker-dialog {
    transform: translateY(-25%);
    z-index: 2;

    &--right {
      margin-left: (($caretSize * 2.5) - 1px);
    }
  }
}

.timeline-marker-card {
  &.v-card {
    min-width: 13em;
    background-color: var(--color-element-layer1);
    color: var(--color-text-theme);
    opacity: 1;

    .v-card {
      &__title {
        justify-content: space-between;

        .v-btn-toggle {
          max-width: 30%;
          .nav-btn {
            max-width: 50%;
          }
        }
        .heading {
          width: 70%;
          font-weight: 700;
          text-align: center;
        }
      }
      &__text {
        color: var(--color-text-theme);

        .data-report {
          display: flex;
          column-gap: 0.5em;
          margin-bottom: 0.5em;
          font-size: var(--font-size-sm);

          .label-cf-override {
            width: 12em;
          }

          .label-cf-override {
            width: 15em;
          }

          .label,
          .icon-as-label,
          .data {
            line-height: 1.35;
          }
          .label,
          .data {
            color: currentcolor;
          }

          .label,
          .icon-as-label {
            width: 5em;
            text-align: left;
            margin-right: 0;
          }
          .label {
            word-break: break-word;
            opacity: 0.8;

            &:not(.icon-as-label):after {
              content: " :";
            }
          }
          .icon-as-label {
            display: flex;
            justify-content: flex-start;
            align-items: flex-start;
            padding-right: 0.25rem;
            opacity: 0.7;

            .v-icon {
              font-size: 1.15em;
              color: currentcolor;
            }
          }
          .data {
            display: flex;
            justify-content: flex-end;
            align-items: flex-end;
            width: calc(100% - 5em);

            time {
              text-align: right;
            }
          }
        }
      }
    }
  }
}

.marker-btn {
  &.v-btn {
    position: absolute;
    z-index: 1;
    height: auto;
    min-width: 0;
    top: ($caretSize - 5px);
    padding: 2px;
    background-color: transparent;
    transform: translateX(-50%);
    transition: transform 0.3s ease;
    border-radius: 0;

    ::v-deep .v-btn__content {
      opacity: 1 !important;
    }
    &:hover,
    &:focus,
    &--active {
      z-index: 3;
      padding-top: 1px;
      border-top: 2px solid var(--color-primary);
    }
  }
  // triangle shape
  &__caret {
    display: block;
    width: 0;
    height: 0;
    border-top: $caretSize solid var(--color-inverted-container-theme);
    border-right: $caretSize solid transparent;
    border-left: $caretSize solid transparent;
    transition: border-color 0.2s linear;

    @at-root .marker-btn--active #{&} {
      border-top-color: var(--color-primary);
    }
  }
  // conversion factor
  &__cfo {
    display: block;
    width: 0;
    height: 0;
    margin-top: -15px;

    @at-root .marker-btn--active #{&} {
      color: var(--color-primary);
    }
  }
}
.label-ds {
  width: 80%;
}
.label-cf {
  width: 20% !important;
}
</style>
